import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Navbar from '../Navbar';
import { fulfillmentsApi, rentalsApi } from '../../lib/api';
import { calculateDistanceMeters } from '../../utils/geo';
import OwnerConfirmationModal from './OwnerConfirmationModal';
import { QRCodeSVG } from 'qrcode.react';
import { assetUrl } from '../../utils/assetUrl'

interface FulfillmentDetails {
  id: string;
  status: string;
  fulfillmentType: string;
  pickupDetails?: {
    pickupWindowStart: string;
    pickupWindowEnd: string;
    address: string;
    instructions?: string;
    qrCode?: string;
    qrCodeExpiry?: string;
    geofenceRadiusMeters?: number;
    latitude?: number;
    longitude?: number;
    lenderVerifiedAt?: string | null;
    prePickupPhotos?: Array<{ url: string; takenBy?: string }> | null;
    renterVerifiedAt?: string | null;
  };
  rental?: {
    id: string;
    status: string;
    isPaid: boolean;
    endDate: string;
    estimatedPickupDistanceMeters?: number | null;
    item: {
      title: string;
      photos?: { url: string }[];
    };
    owner: {
      id: string;
      firstName: string;
      lastName: string;
      phone?: string;
    };
    renter: {
      id: string;
      firstName: string;
      lastName: string;
      phone?: string;
    };
  };
}

const STATUS_STEPS = [
  { status: 'PENDING', label: 'Pending Confirmation', icon: 'mdi-clock-alert', color: 'text-amber-500' },
  { status: 'CONFIRMED', label: 'Confirmed', icon: 'mdi-check-circle', color: 'text-blue-500' },
  { status: 'READY_FOR_PICKUP', label: 'Ready for Pickup', icon: 'mdi-package-variant', color: 'text-purple-500' },
  { status: 'PICKED_UP', label: 'Picked Up', icon: 'mdi-truck-check', color: 'text-green-500' },
  { status: 'COMPLETED', label: 'Completed', icon: 'mdi-check-circle-outline', color: 'text-green-600' },
];

export default function PickupTracker() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [fulfillment, setFulfillment] = useState<FulfillmentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [distance, setDistance] = useState<number | null>(null);
  const [isWithinGeofence, setIsWithinGeofence] = useState(false);
  const [onMyWaySent, setOnMyWaySent] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmingPickup, setConfirmingPickup] = useState(false);
  const [confirmingHandshake, setConfirmingHandshake] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [manualConfirmOpen, setManualConfirmOpen] = useState(false);
  const [confirmWindowOpen, setConfirmWindowOpen] = useState(false);
  const lastLoggedGeoErrorCode = useRef<number | null>(null);
  // Whether we can go straight to watchPosition (permission already granted
  // previously, so the browser won't show a prompt anyway -- asking first
  // would just be an extra click with nothing to explain) or need to show an
  // in-app explanation before the native permission dialog fires for the
  // first time. Starts 'checking' while we query the current permission
  // state; defaults to 'needs-consent' if that query is unsupported or
  // fails, since explaining first is the safer default.
  const [locationConsent, setLocationConsent] = useState<'checking' | 'granted' | 'needs-consent'>('checking');
  const geoWatchIdRef = useRef<number | null>(null);

  const miles = distance === null ? null : distance / 1609.34;
  const tooFar = miles !== null && miles > 100;
  // Rough driving ETA from straight-line distance (avg ~35 mph city/suburban).
  const etaMinutes = miles === null ? null : Math.max(5, Math.round((miles / 35) * 60));

  // Live GPS is the source of truth when we have it. When we don't (denied,
  // unavailable, indoors), falling back to "no distance check at all" let a
  // renter thousands of miles away manually confirm pickup with zero friction --
  // the same 100mi rule GPS enforces should hold either way. We already compute
  // an estimate (renter's profile location vs. the owner's pickup spot) at
  // request time for the pre-approval heads-up notification (rentalController's
  // `estimatedPickupDistanceMeters`); reuse it here instead of re-geocoding on
  // every poll.
  const estimatedMeters = fulfillment?.rental?.estimatedPickupDistanceMeters;
  const fallbackMiles = distance === null && estimatedMeters != null ? estimatedMeters / 1609.34 : null;
  const fallbackTooFar = fallbackMiles !== null && fallbackMiles > 100;

  useEffect(() => {
    if (id) {
      fetchFulfillment();
    }
  }, [id]);

  // Without this, a tab left open before the other party confirms/marks ready
  // just shows a stale snapshot forever -- the status only ever updated after
  // an action the CURRENT user took, never one the other party took elsewhere.
  useEffect(() => {
    if (!id || !fulfillment) return;
    if (fulfillment.status === 'COMPLETED' || fulfillment.status === 'CANCELLED') return;
    const interval = setInterval(() => fetchFulfillment(true), 10000);
    return () => clearInterval(interval);
  }, [id, fulfillment?.status]);

  // Depend on the lat/lng values, not the whole `fulfillment` object -- the 10s
  // poll replaces `fulfillment` with a brand-new object every cycle even when
  // nothing changed, and useEffect's reference-equality check would otherwise
  // tear down and restart the geolocation watch every ~10s. Combined with
  // enableHighAccuracy+a 5s timeout, a watch that never gets to run for more
  // than 10s straight reliably times out before it can acquire a fix, which is
  // why this was flooding the console with "Position acquisition timed out".
  const pickupLat = fulfillment?.pickupDetails?.latitude;
  const pickupLng = fulfillment?.pickupDetails?.longitude;
  useEffect(() => {
    if (!pickupLat || !pickupLng) return;

    let cancelled = false;
    const beginWatch = () => {
      if (cancelled) return;
      setLocationConsent('granted');
      geoWatchIdRef.current = watchLocation();
    };

    // navigator.permissions isn't universally supported (notably older
    // Safari) -- fall through to 'needs-consent' (explain first) in that
    // case, same as a query that errors.
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName }).then((status) => {
        if (cancelled) return;
        if (status.state === 'granted') {
          beginWatch();
        } else {
          setLocationConsent('needs-consent');
        }
      }).catch(() => {
        if (!cancelled) setLocationConsent('needs-consent');
      });
    } else {
      setLocationConsent('needs-consent');
    }

    return () => {
      cancelled = true;
      if (geoWatchIdRef.current !== null) {
        navigator.geolocation.clearWatch(geoWatchIdRef.current);
        geoWatchIdRef.current = null;
      }
    };
  }, [pickupLat, pickupLng]);

  // Called from the explanatory card's button -- this is the user action the
  // native permission prompt now fires in direct response to, instead of
  // firing silently the instant the page loads.
  const handleStartLocationCheck = () => {
    setLocationConsent('granted');
    geoWatchIdRef.current = watchLocation();
  };

  // isBackgroundRefresh keeps the 10s poll from toggling `loading`, which gates
  // the whole page render -- without it, every poll unmounted the entire
  // Navbar/timeline/buttons tree in favor of the "Loading..." screen, which
  // looked exactly like the page reloading itself every 10 seconds.
  const fetchFulfillment = async (isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) setLoading(true);
      const response = await fulfillmentsApi.get(id!);
      if (response.data.success) {
        setFulfillment(response.data.data);
      }
    } catch (err: any) {
      if (!isBackgroundRefresh) setError(err?.response?.data?.error || 'Failed to load pickup details');
    } finally {
      if (!isBackgroundRefresh) setLoading(false);
    }
  };

  const watchLocation = (): number | null => {
    if (!navigator.geolocation) {
      setLocationError("Your browser doesn't support location. You can still confirm pickup manually below.");
      return null;
    }

    return navigator.geolocation.watchPosition(
      (position) => {
        lastLoggedGeoErrorCode.current = null;
        setLocationError(null);
        calculateDistance(position);
      },
      (err) => {
        // The browser retries watchPosition on its own after a timeout, which
        // reliably re-fires this callback if it can't get a fix (e.g. no GPS,
        // indoors) -- log each distinct error once rather than flooding the
        // console with the same "timed out" message every retry. The UI
        // already surfaces this via locationError below.
        if (lastLoggedGeoErrorCode.current !== err.code) {
          console.error('Geolocation error:', err);
          lastLoggedGeoErrorCode.current = err.code;
        }
        setLocationError(
          err.code === err.PERMISSION_DENIED
            ? "Location access denied. You can still confirm pickup manually below."
            : "Couldn't determine your location. You can still confirm pickup manually below."
        );
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
    );
  };

  const calculateDistance = (position: GeolocationPosition) => {
    if (!fulfillment?.pickupDetails?.latitude || !fulfillment?.pickupDetails?.longitude) return;

    const dist = calculateDistanceMeters(
      position.coords.latitude,
      position.coords.longitude,
      fulfillment.pickupDetails.latitude,
      fulfillment.pickupDetails.longitude
    );
    setDistance(dist);

    const radius = fulfillment.pickupDetails.geofenceRadiusMeters || 100;
    setIsWithinGeofence(dist <= radius);
  };

  const handleOnMyWay = async () => {
    if (!fulfillment?.rental?.id) return;
    setActionError(null);
    try {
      const eta = etaMinutes !== null ? `~${etaMinutes} min` : 'soon';
      const itemTitle = fulfillment.rental?.item?.title || 'the item';
      await rentalsApi.sendMessage(
        fulfillment.rental.id,
        `I'm on my way to pick up "${itemTitle}". ETA ${eta}.`
      );
      setOnMyWaySent(true);
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Failed to notify the owner. Try again.');
    }
  };

  const handleCancel = async () => {
    if (!fulfillment?.rental?.id) return;
    setCancelling(true);
    setActionError(null);
    try {
      await rentalsApi.cancel(fulfillment.rental.id);
      navigate('/my-rentals');
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Failed to cancel the rental. Try again.');
      setCancelling(false);
    }
  };

  const handleRequestShipping = async () => {
    if (!fulfillment?.rental?.id) return;
    setActionError(null);
    try {
      await rentalsApi.sendMessage(
        fulfillment.rental.id,
        'The pickup location is too far for me to drive. Can we switch to shipping? I can pay the additional shipping cost.'
      );
      setOnMyWaySent(true);
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Failed to message the owner. Try again.');
    }
  };

  // The renter's confirmation comes with photos of the item (its condition when it left the
  // owner), so with none on file the button takes them to the camera first.
  const renterHasPhotos = ((fulfillment?.pickupDetails?.prePickupPhotos as any[]) || []).some((p) => p?.takenBy === 'renter');

  const handleConfirmHandshake = async () => {
    if (!id) return;
    if (!renterHasPhotos) {
      navigate(`/fulfillment/${id}/inspect`);
      return;
    }
    setConfirmingHandshake(true);
    setActionError(null);
    try {
      // The owner already confirmed via the two-party handshake -- complete it the
      // same way (not the single-party updateStatus path below) so the audit trail
      // and the owner's own "pickup confirmed" notification/email fire correctly.
      await fulfillmentsApi.confirmHandshake(id);
      navigate(`/fulfillment/${id}/handshake`);
    } catch (err: any) {
      if (err?.response?.data?.code === 'PRE_PICKUP_PHOTOS_REQUIRED') {
        navigate(`/fulfillment/${id}/inspect`);
        return;
      }
      setActionError(err?.response?.data?.error || 'Failed to confirm pickup. Try again.');
      setConfirmingHandshake(false);
    }
  };

  const handleConfirmPickup = async () => {
    if (!id) return;
    if (!renterHasPhotos) {
      navigate(`/fulfillment/${id}/inspect`);
      return;
    }
    setConfirmingPickup(true);
    setActionError(null);
    try {
      // Records the renter's side only. Pickup is complete once the owner has signed off too;
      // the renter can't start the rental by themselves (for example when GPS is unavailable).
      const res = await fulfillmentsApi.confirmHandshake(id);
      if (res.data?.data?.status === 'PICKED_UP') {
        navigate(`/fulfillment/${id}/handshake`);
        return;
      }
      await fetchFulfillment(true);
      setConfirmingPickup(false);
    } catch (err: any) {
      setActionError(err?.response?.data?.error || 'Failed to confirm pickup. Try again.');
      setConfirmingPickup(false);
    }
  };

  const getCurrentStepIndex = () => {
    if (!fulfillment) return 0;
    const index = STATUS_STEPS.findIndex(step => step.status === fulfillment.status);
    return index >= 0 ? index : 0;
  };

  const formatTimeRemaining = () => {
    if (!fulfillment?.pickupDetails?.pickupWindowStart) return '';
    const start = new Date(fulfillment.pickupDetails.pickupWindowStart);
    const now = new Date();
    const diff = start.getTime() - now.getTime();

    if (diff <= 0) return 'Pickup window has started';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''} until pickup`;
    }
    return `${hours}h ${minutes}m until pickup window`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[var(--text-secondary)]">Loading pickup details...</div>
      </div>
    );
  }

  if (error || !fulfillment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">{error || 'Pickup not found'}</div>
      </div>
    );
  }

  const currentStep = getCurrentStepIndex();
  const isOwner = user?.id === fulfillment.rental?.owner?.id;
  const isPending = fulfillment.status === 'PENDING';
  const isConfirmed = fulfillment.status === 'CONFIRMED';
  const isReadyForPickup = fulfillment.status === 'READY_FOR_PICKUP';
  const isCompleted = fulfillment.status === 'COMPLETED';
  // PICKUP fulfillments deliberately skip a RETURNED fulfillment status -- there's no
  // shipping leg, so the state machine goes straight PICKED_UP -> COMPLETED once the
  // owner confirms (see FulfillmentService's VALID_TRANSITIONS/completeFulfillment).
  // That left a real gap here: markAsReturned only syncs Fulfillment.status for
  // DELIVERY-type rentals, so a pickup rental sitting at Rental.status RETURNED (renter
  // reported it back, owner hasn't confirmed yet) still has fulfillment.status stuck at
  // PICKED_UP -- indistinguishable from "still actively rented" without this check.
  const isReturnedAwaitingConfirmation = fulfillment.status === 'PICKED_UP' && fulfillment.rental?.status === 'RETURNED';
  const isPickedUp = fulfillment.status === 'PICKED_UP' && !isReturnedAwaitingConfirmation;
  const isActiveHandoff = isConfirmed || isReadyForPickup;
  const canShowQR = isActiveHandoff && !isOwner;
  // The backend rejects PENDING -> CONFIRMED until the rental is paid (rentalController's
  // /confirm endpoint and FulfillmentService.transitionStatus both enforce this), but this
  // button used to render regardless -- an owner clicking it before the renter paid got a
  // confusing rejection instead of the button simply not being there yet.
  const isAwaitingPayment = isPending && isOwner && fulfillment.rental?.isPaid === false;
  const canConfirmWindow = isPending && isOwner && fulfillment.rental?.isPaid === true;
  const canMarkReady = isConfirmed && isOwner;
  const canScanQR = isReadyForPickup && isOwner;
  const canConfirmPickup = isReadyForPickup && !isOwner;
  const canSayOnMyWay = isReadyForPickup && !isOwner && !tooFar && !fallbackTooFar && !onMyWaySent
    && fulfillment.pickupDetails?.lenderVerifiedAt == null;
  const hasDropOffAddress = (user?.metadata?.locations?.['drop-off']?.length ?? 0) > 0;
  const isRenterView = !isOwner;
  // The owner already confirmed their side of the two-party digital handshake (see
  // DigitalHandshake.tsx / POST /fulfillments/:id/handshake) and is waiting on the
  // renter -- in that case, walking the renter through a GPS location check before
  // letting them confirm is redundant ceremony on top of ceremony. Skip straight to
  // the action instead of one of the GPS-flow panels below.
  // The renter already said they have the item and is waiting for the owner to sign off.
  const awaitingOwner = canConfirmPickup
    && fulfillment.pickupDetails?.renterVerifiedAt != null
    && fulfillment.pickupDetails?.lenderVerifiedAt == null;
  const ownerNeedsToSignOff = isOwner && isReadyForPickup
    && fulfillment.pickupDetails?.renterVerifiedAt != null
    && fulfillment.pickupDetails?.lenderVerifiedAt == null;
  const handshakePending = canConfirmPickup
    && fulfillment.pickupDetails?.lenderVerifiedAt != null
    && fulfillment.pickupDetails?.renterVerifiedAt == null;

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-primary)]">
      <Navbar />
      <main className="mt-[--navbar-height] min-h-[calc(100vh-var(--navbar-height))] py-6">
        <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
          <div className="flex items-center gap-4">
            {fulfillment.rental?.item.photos?.[0] ? (
              <img
                src={assetUrl(fulfillment.rental.item.photos[0].url)}
                alt={fulfillment.rental.item.title}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 bg-[var(--bg-tint)] rounded-lg flex items-center justify-center">
                <i className="mdi mdi-items text-2xl text-[var(--forest-green)]"></i>
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-[var(--text-primary)]">
                {fulfillment.rental?.item.title}
              </h1>
              <p className="text-sm text-[var(--text-secondary)]">
                {formatTimeRemaining()}
              </p>
            </div>
          </div>
        </div>

        {/* Pickup Window Display */}
        {fulfillment.pickupDetails?.pickupWindowStart && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
            <div className="flex items-center gap-3">
              <i className="mdi mdi-calendar-clock text-[var(--forest-green)] text-xl"></i>
              <div>
                <p className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wide">Pickup Window</p>
                <p className="text-sm font-semibold text-[var(--text-primary)]">
                  {new Date(fulfillment.pickupDetails.pickupWindowStart).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                  })}
                  {' · '}
                  {new Date(fulfillment.pickupDetails.pickupWindowStart).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                  {fulfillment.pickupDetails.pickupWindowEnd && (
                    <>
                      {' – '}
                      {new Date(fulfillment.pickupDetails.pickupWindowEnd).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
          <div className="flex items-center justify-between">
            {STATUS_STEPS.map((step, index) => (
              <div key={step.status} className="relative flex flex-col items-center flex-1">
                {index < STATUS_STEPS.length - 1 && (
                  // Anchored to each circle's actual outer edge, not its center -- the
                  // circle is w-10 (40px, 20px radius), so the line starts 20px right of
                  // this column's center (past this circle's rim) and stops 20px short
                  // of the far end (before the next circle's rim), sitting purely in the
                  // gap between them. This doesn't depend on the circle's fill visually
                  // masking anything (an earlier attempt at that wasn't reliable); the
                  // line simply never reaches under either circle in the first place.
                  <div className={`absolute h-0.5 top-5 left-[calc(50%+20px)] w-[calc(100%-40px)] ${
                    index < currentStep ? 'bg-[var(--forest-green)]' : 'bg-[var(--border)]'
                  }`}></div>
                )}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  index <= currentStep ? 'bg-[var(--forest-green)] text-white' : 'bg-[var(--bg-tint)] text-[var(--text-muted)]'
                }`}>
                  <i className={`mdi ${step.icon} text-lg`}></i>
                </div>
                {/* w-full (not a flat max-w-[80px]) so the label wraps within its own
                    column's real width, whatever that happens to be at any given screen
                    size -- flex-1 columns can be narrower than 80px, and a fixed cap wider
                    than the column let unbreakable single words ("Completed") overflow
                    into the next column and run directly into its label with zero gap. */}
                <span className={`text-xs leading-4 mt-2 text-center w-full px-0.5 min-h-8 flex items-start justify-center ${
                  index <= currentStep ? 'text-[var(--text-primary)] font-medium' : 'text-[var(--text-muted)]'
                }`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Waiting on the owner to confirm a pickup window/QR code. The owner gets an
            actual button here now -- this page used to just tell them to confirm a
            window with no way to do it, forcing a detour through the Dashboard's
            separate "Pending Confirmations" widget. */}
        {isPending && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <p className="text-amber-800 text-sm flex items-center gap-2">
              <i className="mdi mdi-clock-outline"></i>
              {isAwaitingPayment
                ? "Waiting for the renter to pay before you can confirm a pickup window."
                : isOwner
                ? "Confirm a pickup window to generate the renter's QR code and move this forward."
                : "Waiting for the owner to confirm a pickup window. You'll be notified as soon as it's ready."}
            </p>
            {canConfirmWindow && (
              <button
                onClick={() => setConfirmWindowOpen(true)}
                className="btn btn-primary w-full mt-3"
              >
                <i className="mdi mdi-calendar-check mr-2"></i>
                Confirm Pickup Window
              </button>
            )}
          </div>
        )}

        {/* Location Status — only relevant for the renter traveling to the pickup.
            When too far, this panel also absorbs the cancel/switch-to-shipping
            options in place, rather than repeating the same distance/address
            info in a second panel right below it. */}
        {isRenterView && distance !== null && (
          <div className={`rounded-xl border p-4 mb-4 ${
            isWithinGeofence
              ? 'bg-green-50 border-green-200'
              : tooFar
                ? 'bg-red-50 border-red-200'
                : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-center gap-3">
              <i className={`mdi ${isWithinGeofence ? 'mdi-map-marker-check text-green-600' : tooFar ? 'mdi-alert text-red-600' : 'mdi-map-marker-distance text-amber-600'} text-2xl`}></i>
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${isWithinGeofence ? 'text-green-800' : tooFar ? 'text-red-800' : 'text-amber-800'}`}>
                  {isWithinGeofence
                    ? 'You are at the pickup location!'
                    : tooFar
                      ? `${Math.round(miles!).toLocaleString()} mi away — too far to pick up`
                      : `${Math.round(miles!).toLocaleString()} mi away from pickup`}
                </p>
                <p className="text-sm text-[var(--text-secondary)] truncate">
                  {fulfillment.pickupDetails?.address}
                </p>
                {/* Directions don't make sense once we've already told the renter it's too far to drive. */}
                {!tooFar && (fulfillment.pickupDetails?.address || (fulfillment.pickupDetails?.latitude && fulfillment.pickupDetails?.longitude)) && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${
                      // Prefer the actual street address — the lat/lng on file may only be a
                      // city-centroid approximation, not the real pickup address.
                      fulfillment.pickupDetails?.address
                        ? encodeURIComponent(fulfillment.pickupDetails.address)
                        : `${fulfillment.pickupDetails!.latitude},${fulfillment.pickupDetails!.longitude}`
                    }`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[var(--forest-green)] font-medium hover:underline mt-1"
                  >
                    <i className="mdi mdi-directions"></i>
                    Get directions
                  </a>
                )}
              </div>
            </div>

            {tooFar && (
              <div className="mt-4 pt-4 border-t border-red-200">
                <p className="text-sm text-red-800 mb-4">
                  This pickup is more than 100 miles away. You can cancel the rental, or ask the owner to switch to shipping (additional cost may apply).
                </p>
                {!hasDropOffAddress && (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                    You'll also need a shipping address on file before the owner can ship to you.{' '}
                    <a href="/profile" className="font-semibold underline">Add one in Profile &gt; Locations</a> first.
                  </p>
                )}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleRequestShipping}
                    disabled={onMyWaySent}
                    className="btn btn-primary flex-1"
                  >
                    <i className="mdi mdi-truck mr-2"></i>
                    {onMyWaySent ? 'Request sent' : 'Request shipping instead'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="btn btn-danger flex-1"
                  >
                    <i className="mdi mdi-cancel mr-2"></i>
                    {cancelling ? 'Cancelling…' : 'Cancel order'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Returned, awaiting the owner's confirmation -- see isReturnedAwaitingConfirmation's
            definition above for why this needs to be its own state. */}
        {isReturnedAwaitingConfirmation && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-4">
            <div className="flex items-center gap-3">
              <i className="mdi mdi-keyboard-return text-amber-600 text-2xl"></i>
              <div>
                <p className="font-medium text-amber-800">Return reported</p>
                <p className="text-sm text-amber-700 mt-1">
                  {isOwner
                    ? 'The renter reported returning the item. Confirm receipt on the rental page to complete the rental.'
                    : "You've reported this item as returned. Waiting for the owner to confirm receipt."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Picked up / completed summary */}
        {(isPickedUp || isCompleted) && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
            <div className="flex items-center gap-3">
              <i className="mdi mdi-check-circle text-green-600 text-2xl"></i>
              <div>
                <p className="font-medium text-green-800">
                  {isCompleted ? 'Rental completed' : 'Item picked up'}
                </p>
                <p className="text-sm text-green-700 mt-1">
                  {isCompleted
                    ? 'This rental has been completed. Thanks for using igotthatus.com!'
                    : isOwner
                      ? `Item is out for rent with ${fulfillment.rental?.renter?.firstName || 'the renter'}${fulfillment.rental?.endDate ? ` until ${new Date(fulfillment.rental.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}.`
                      : 'The item is in your possession. Have a great rental!'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Renter: start the pickup — "I'm on my way" notifies the owner with ETA (no dead end) */}
        {isRenterView && canSayOnMyWay && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Ready to head out?</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              {etaMinutes !== null ? `About ${etaMinutes} min drive (${Math.round(miles!).toLocaleString()} mi). ` : ''}
              Let the owner know you're on your way so they can have the item ready.
            </p>
            <button onClick={handleOnMyWay} className="btn btn-primary w-full">
              <i className="mdi mdi-car mr-2"></i>
              I'm on my way
            </button>
          </div>
        )}
        {isRenterView && onMyWaySent && !tooFar && !isPickedUp && !isCompleted && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
            <p className="text-green-800 text-sm flex items-center gap-2">
              <i className="mdi mdi-check-circle"></i>
              The owner has been notified you're on your way{etaMinutes !== null ? ` (ETA ~${etaMinutes} min)` : ''}.
            </p>
          </div>
        )}

        {/* Owner already confirmed their side of the two-party handshake -- skip the
            GPS location dance entirely (it's redundant ceremony once the owner has
            already vouched for the handoff) and let the renter confirm directly,
            with an escape hatch to cancel or open the full two-party status screen. */}
        {isRenterView && handshakePending && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Owner confirmed pickup</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              {fulfillment.rental?.owner?.firstName || 'The owner'} has confirmed their side of the handoff.
              Confirm yours once you have the item in hand.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <button
                onClick={handleConfirmHandshake}
                disabled={confirmingHandshake}
                className="btn btn-primary flex-1"
              >
                <i className="mdi mdi-handshake mr-2"></i>
                {confirmingHandshake ? 'Confirming…' : renterHasPhotos ? "Yes, I've picked up the item" : 'I have the item, take photos'}
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="btn btn-danger flex-1"
              >
                <i className="mdi mdi-cancel mr-2"></i>
                {cancelling ? 'Cancelling…' : 'Cancel order'}
              </button>
            </div>
            <button
              onClick={() => navigate(`/fulfillment/${id}/handshake`)}
              className="text-sm text-[var(--forest-green)] font-medium underline"
            >
              View full confirmation status
            </button>
          </div>
        )}

        {isRenterView && awaitingOwner && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Waiting for the owner</h3>
            <p className="text-sm text-[var(--text-secondary)]">
              You confirmed you have the item. The rental starts once {fulfillment.rental?.owner?.firstName || 'the owner'} signs off on the handover.
            </p>
          </div>
        )}

        {ownerNeedsToSignOff && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">Renter says they have the item</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Confirm the handover to start the rental. Scan their QR code or confirm directly.
            </p>
            <button onClick={() => navigate(`/fulfillment/${id}/handshake`)} className="btn btn-primary">
              <i className="mdi mdi-handshake mr-2"></i>
              Confirm handover
            </button>
          </div>
        )}

        {/* Renter: confirm pickup when at the location */}
        {isRenterView && !handshakePending && !awaitingOwner && canConfirmPickup && isWithinGeofence && !tooFar && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">At the pickup location?</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Tap below once you have the item in hand. We'll ask for a quick photo and notes next.
            </p>
            <button
              onClick={handleConfirmPickup}
              disabled={confirmingPickup}
              className="btn btn-primary w-full"
            >
              <i className="mdi mdi-check-circle mr-2"></i>
              {confirmingPickup ? 'Confirming…' : renterHasPhotos ? "I've picked up the item" : 'I have the item, take photos'}
            </button>
          </div>
        )}

        {/* Explain why we're about to ask for location access before the native
            browser permission dialog interrupts -- it used to fire the instant
            this page loaded, with zero in-app context, which was disorienting
            especially the first time. Skipped entirely when permission was
            already granted in an earlier visit, since the browser won't show a
            prompt anyway and asking first would just be a redundant extra tap. */}
        {isRenterView && !handshakePending && !awaitingOwner && canConfirmPickup && locationConsent === 'needs-consent' && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">At the pickup location?</h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              We'll check your device's location to confirm you're at the pickup spot. Your browser will ask you to allow this next.
            </p>
            <button onClick={handleStartLocationCheck} className="btn btn-primary w-full">
              <i className="mdi mdi-map-marker-radius mr-2"></i>
              Check my location
            </button>
          </div>
        )}

        {/* Renter: manual fallback when we can't confirm GPS is inside the geofence
            (permission denied, poor accuracy, indoor pickup, etc.) — avoids a dead end.
            Gated on locationConsent === 'granted' so this doesn't appear alongside
            the explanatory card above before the renter has actually responded to it. */}
        {isRenterView && !handshakePending && !awaitingOwner && canConfirmPickup && locationConsent === 'granted' && !isWithinGeofence && !tooFar && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">At the pickup location?</h3>

            {/* Same 100mi rule GPS enforces, applied from the profile-location estimate
                instead -- a renter who is genuinely far away shouldn't get an easier
                path to confirming pickup just because their GPS is off/denied. Only
                blocks when we actually have an estimate; if we don't (no profile
                location on file, geocoding failed at request time), there's nothing to
                enforce and we fall through to the honesty-based manual confirm below. */}
            {distance === null && fallbackTooFar ? (
              <>
                <p className="text-sm text-red-800 mb-4">
                  Based on your profile address, this pickup looks to be about{' '}
                  {Math.round(fallbackMiles!).toLocaleString()} mi away — too far to confirm pickup without a live
                  location check. You can request shipping instead, or cancel.
                </p>
                {!hasDropOffAddress && (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                    You'll also need a shipping address on file before the owner can ship to you.{' '}
                    <a href="/profile" className="font-semibold underline">Add one in Profile &gt; Locations</a> first.
                  </p>
                )}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleRequestShipping}
                    disabled={onMyWaySent}
                    className="btn btn-primary flex-1"
                  >
                    <i className="mdi mdi-truck mr-2"></i>
                    {onMyWaySent ? 'Request sent' : 'Request shipping instead'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={cancelling}
                    className="btn btn-danger flex-1"
                  >
                    <i className="mdi mdi-cancel mr-2"></i>
                    {cancelling ? 'Cancelling…' : 'Cancel order'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                  {locationError || "We couldn't confirm you're at the pickup location using GPS."}
                  {fallbackMiles !== null && (
                    ` Based on your profile address, you look to be about ${Math.round(fallbackMiles).toLocaleString()} mi away.`
                  )}
                </p>
                {!manualConfirmOpen ? (
                  <button
                    onClick={() => setManualConfirmOpen(true)}
                    className="btn btn-secondary w-full"
                  >
                    <i className="mdi mdi-map-marker-question mr-2"></i>
                    Can't verify location — confirm manually
                  </button>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800 mb-3">
                      Only confirm if you're actually at the pickup address with the item in hand.
                      You'll take photos of the item next; the rental can't start without them.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={handleConfirmPickup}
                        disabled={confirmingPickup}
                        className="btn btn-primary flex-1"
                      >
                        {confirmingPickup ? 'Confirming…' : renterHasPhotos ? "Yes, I've picked up the item" : 'I have the item, take photos'}
                      </button>
                      <button
                        onClick={() => setManualConfirmOpen(false)}
                        className="btn btn-secondary flex-1"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Without a GPS reading or a usable estimate, there's genuinely nothing to
                    check the distance against -- this is the honesty-based safety net for
                    that case only, not a substitute for the enforced block above. */}
                {distance === null && (
                  <div className="mt-4 pt-4 border-t border-[var(--border)]">
                    <p className="text-sm text-[var(--text-secondary)] mb-3">
                      Not actually going to make it to the pickup?
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={handleRequestShipping}
                        disabled={onMyWaySent}
                        className="btn btn-primary flex-1"
                      >
                        <i className="mdi mdi-truck mr-2"></i>
                        {onMyWaySent ? 'Request sent' : 'Request shipping instead'}
                      </button>
                      <button
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="btn btn-danger flex-1"
                      >
                        <i className="mdi mdi-cancel mr-2"></i>
                        {cancelling ? 'Cancelling…' : 'Cancel order'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* QR Code Section — only the renter needs to show a code to the owner. This
            reads straight from the fulfillment record, so it's available every time
            the renter loads this page (e.g. after navigating away and back via
            "Track pickup" on the rental) for as long as the fulfillment stays
            CONFIRMED/READY_FOR_PICKUP -- nothing here expires or gets consumed by a
            scan attempt. */}
        {isRenterView && canShowQR && fulfillment.pickupDetails?.qrCode && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
              Pickup QR Code
            </h3>
            <div className="bg-[var(--bg-tint)] rounded-lg p-8 flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <QRCodeSVG value={fulfillment.pickupDetails.qrCode} size={200} level="M" />
              </div>
              <div className="text-sm font-mono tracking-wider text-center text-[var(--text-secondary)] mt-4">
                {fulfillment.pickupDetails.qrCode}
              </div>
              <div className="text-xs text-center text-[var(--text-muted)] mt-1">
                Let the owner scan this, or read them the code above
              </div>
              <p className="text-sm text-[var(--text-secondary)] mt-4 text-center">
                Expires: {new Date(fulfillment.pickupDetails.qrCodeExpiry!).toLocaleString()}
              </p>
            </div>
          </div>
        )}

        {/* Owner Actions */}
        {canMarkReady && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
              Ready for Pickup?
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Mark this rental as ready so the renter can pick it up.
            </p>
            <button
              onClick={async () => {
                try {
                  await fulfillmentsApi.updateStatus(id!, 'READY_FOR_PICKUP');
                  fetchFulfillment();
                } catch (err: any) {
                  setError(err?.response?.data?.error || 'Failed to mark as ready');
                }
              }}
              className="btn btn-primary w-full"
            >
              <i className="mdi mdi-package-variant mr-2"></i>
              Mark Ready for Pickup
            </button>
          </div>
        )}

        {canScanQR && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-6 mb-4">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
              Scan Renter's QR Code
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-4">
              Scan the renter's QR code to verify their identity and complete pickup.
            </p>
            <button
              onClick={() => navigate(`/fulfillment/${id}/scan`)}
              className="btn btn-primary w-full"
            >
              <i className="mdi mdi-qrcode-scan mr-2"></i>
              Scan QR Code
            </button>
          </div>
        )}

        {/* Owner / Renter contact info */}
        <div className="bg-white rounded-xl border border-[var(--border)] p-4 mb-4">
          <p className="text-xs text-[var(--text-muted)] font-medium uppercase tracking-wide mb-3">
            {isOwner ? 'Renter contact' : 'Owner contact'}
          </p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--bg-tint)] flex items-center justify-center text-[var(--forest-green)]">
              <i className="mdi mdi-account"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[var(--text-primary)] truncate">
                {isOwner
                  ? `${fulfillment.rental?.renter?.firstName || ''} ${fulfillment.rental?.renter?.lastName || ''}`.trim()
                  : `${fulfillment.rental?.owner?.firstName || ''} ${fulfillment.rental?.owner?.lastName || ''}`.trim()}
              </p>
              {(isOwner ? fulfillment.rental?.renter?.phone : fulfillment.rental?.owner?.phone) && (
                <p className="text-sm text-[var(--text-secondary)]">
                  {isOwner ? fulfillment.rental?.renter?.phone : fulfillment.rental?.owner?.phone}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Pickup Instructions */}
        {fulfillment.pickupDetails?.instructions && (
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 mb-4">
            <div className="flex items-start gap-3">
              <i className="mdi mdi-information text-blue-600 text-xl"></i>
              <div>
                <p className="font-medium text-blue-800">Pickup Instructions</p>
                <p className="text-sm text-blue-700 mt-1">
                  {fulfillment.pickupDetails.instructions}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Message Button */}
        {fulfillment.rental?.id && (
          <div className="bg-white rounded-xl border border-[var(--border)] p-4">
            <button
              onClick={() => navigate(`/rental/${fulfillment.rental?.id}`)}
              className="btn btn-secondary w-full"
            >
              <i className="mdi mdi-message-text mr-2"></i>
              {isOwner ? 'Message Renter' : 'Message Owner'}
            </button>
          </div>
        )}

        {actionError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mt-4 text-sm">
            {actionError}
          </div>
        )}
        </div>
      </main>

      {canConfirmWindow && (
        <OwnerConfirmationModal
          isOpen={confirmWindowOpen}
          onClose={() => setConfirmWindowOpen(false)}
          onConfirmed={() => {
            setConfirmWindowOpen(false);
            fetchFulfillment();
          }}
          fulfillmentId={fulfillment.id}
          rentalId={fulfillment.rental?.id}
          itemTitle={fulfillment.rental?.item.title || ''}
          renterName={`${fulfillment.rental?.renter?.firstName || ''} ${fulfillment.rental?.renter?.lastName || ''}`.trim()}
          pickupAddress={fulfillment.pickupDetails?.address || ''}
          initialPickupDate={fulfillment.pickupDetails?.pickupWindowStart}
        />
      )}
    </div>
  );
}
